import React from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { CustomEventNames } from 'features/analytics';
import { logNavigationClick } from 'features/navigation/utils/analytics';
import { LoadingIndicator } from 'features/store/pages/pickup-store-selector/components';
import { useSupportData } from 'features/support/core/hooks/use-support-data';
import { StyleSheet } from 'react-native';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { BlankScreen } from 'components/blank-screen';
import { CardModal } from 'components/card-modal';
import { Box, Typography } from 'components/ui';
import { useRefundEligibility } from 'hooks/refund-eligibility';
import { useOrderDetails } from 'hooks/use-order-details';
import { LoyaltyLoader } from 'pages/loyalty/loyalty-loader';
import { t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';
import { useReorder } from 'state/order/hooks/use-reorder';
import { useUIContext } from 'state/ui';
import { spacing } from 'styles/constants';
import { routes } from 'utils/routing';

import { CartEntry } from './cart-entry';
import { Divider } from './details-modal.styled';
import { PointsEarnedBlock } from './points-earned';
import { ReceiptDetails } from './receipt-details';
import { ReceiptHeadInfo } from './receipt-head-info';
import { useHelpLinkUrlGenerator } from './use-help-link-url-generator';

export const DetailsModal: React.FC = () => {
  const router = useRouter();

  const { rbiOrderId } = useLocalSearchParams<{ rbiOrderId: string }>();

  const { formatCurrencyForLocale } = useUIContext();
  const { order, loading: isOrderLoading, refetch } = useOrderDetails(rbiOrderId);

  const { handleReorderClick, isReorderingDisabled } = useReorder(order);
  const {
    reorder: { reordering },
  } = useOrderContext();

  const { isDeliveryActive } = useRefundEligibility({
    orderId: rbiOrderId,
  });
  const { isSupportDataLoading } = useSupportData();

  const helpLink = useHelpLinkUrlGenerator(rbiOrderId);

  const loyaltyTransaction = order?.loyaltyTransaction;
  const handleNeedHelpClick = () => {
    logNavigationClick(CustomEventNames.BUTTON_CLICK_RECENT_ORDER_NEED_HELP);
    router.navigate(helpLink);
  };

  // TODO: ReactNavigation GST-92 Move away from Modal component
  return (
    <CardModal
      standardPadding
      analyticsEvent="Order Details"
      headerText={t('receipt')}
      scrollView
      footer={
        <>
          {isDeliveryActive ? (
            <ActionButton
              fullWidth
              onPress={() =>
                router.navigate(`${routes.orderConfirmation}/${order.rbiOrderId}?dismissOnly=true`)
              }
              isLoading={isOrderLoading}
            >
              {t('trackOrder')}
            </ActionButton>
          ) : (
            <ActionButton
              fullWidth
              onPress={() => {
                handleReorderClick();
              }}
              isLoading={reordering || isOrderLoading}
              disabled={isReorderingDisabled}
            >
              {t('reorder')}
            </ActionButton>
          )}
          <Box style={styles.reorderDisclaimer} focusable>
            <Typography>
              {t('pricesDiscountsAndAvailabilityAreSbujectToChangeWhenYouReorder')}
            </Typography>
          </Box>

          {isSupportDataLoading && (
            <Box style={styles.needHelpButtonLoadingIndicatorWrapper}>
              <LoadingIndicator />
            </Box>
          )}

          {/*
           * isSupportDataLoading becomes false quickly, so aria-live="polite" on this button is distracting.
           * This will silently appear for screen readers.
           */}
          {!isSupportDataLoading && (
            <ActionButton
              testID="recent-order-need-help"
              fullWidth
              variant={ActionButtonVariants.OUTLINE}
              size={ActionButtonSizes.SMALL}
              onPress={handleNeedHelpClick}
            >
              {t('needHelp')}
            </ActionButton>
          )}
        </>
      }
    >
      {!isOrderLoading && order ? (
        <>
          <Box>
            <ReceiptHeadInfo order={order} />
            <Divider />
          </Box>
          {loyaltyTransaction && (
            <>
              <PointsEarnedBlock lylTransaction={loyaltyTransaction} />
              <Divider />
            </>
          )}
          <Box>
            {order.cart.cartEntries.map((entry, i) => {
              return (
                !entry.isDummyItem && (
                  <React.Fragment key={entry.name! + i}>
                    <CartEntry
                      currencyFormatter={formatCurrencyForLocale}
                      entry={entry}
                      index={i}
                    />
                    <Divider />
                  </React.Fragment>
                )
              );
            })}
          </Box>
          <Box>
            <ReceiptDetails order={order} />
          </Box>
        </>
      ) : isOrderLoading ? (
        <LoyaltyLoader />
      ) : (
        <BlankScreen context={{ reason: 'there is not rbiOrderId' }} onReload={refetch} />
      )}
    </CardModal>
  );
};

const styles = StyleSheet.create({
  needHelpButtonLoadingIndicatorWrapper: {
    borderColor: 'transparent',
    borderWidth: 1,
    padding: spacing.$3,
  },
  reorderDisclaimer: {
    paddingVertical: spacing.$4,
  },
});
